import { Component, OnInit } from '@angular/core';
import { faAngleDown, faAngleUp, faArrowLeft, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { classificationInterface } from 'src/app/util/interfaces/classification';
import { RegisterRequestCustomfieldService } from '../request-customfield/service/request-customfield.service';

interface CustomfieldOrderItem {
	request_field: number;
	field_name: string;
	field_type: string;
	required: number;
	active: number;
	sort_order: number;
	position?: number | string;
}

@Component({
	selector: 'register-form-request-customfield-order',
	templateUrl: './request-customfield-order.component.html',
	styleUrls: ['./request-customfield-order.component.scss'],
})
export class RegisterRequestCustomfieldOrderComponent implements OnInit {
	icons = {
		faAngleDown,
		faAngleUp,
		faArrowLeft,
		faFloppyDisk,
	};

	labels: any = {
		lbl_geral_selecione: 'Selecione',
		lbl_geral_buscar: 'Buscar',
		lbl_geral_salvar: 'Salvar',
		lbl_geral_voltar: 'Voltar',
		lbl_geral_campos_customizados: 'Campos Customizados',
		lbl_ordenar_campos_customizados: 'Ordenar campos customizados',
		lbl_selecione_classificacao: 'Selecione uma classificação para carregar os campos.',
		lbl_arraste_ou_use_controles: 'Organize a sequência de exibição dos campos do chamado.',
		lbl_nenhum_campo: 'Nenhum campo encontrado para esta classificação.',
		lbl_salvo_com_sucesso: 'Salvo com sucesso',
		lbl_an_errour_occour: 'Ocorreu um erro. Tente novamente.',
	};

	classificationOptions = {
		alertField: [],
		selectedCustomer: this.ProfileService.userData().idCustomer,
		hideField: ['impact', 'urgency', 'severity'],
	};

	selectedClassification: classificationInterface = {
		serviceType: [],
		scope: [],
		category: [],
		topic: [],
		solutionArea: [],
	};
	fields: CustomfieldOrderItem[] = [];
	isLoading = false;
	isSaving = false;
	hasSearched = false;

	constructor(
		private ProfileService: ProfileService,
		private LabelsService: LabelsService,
		private RegisterRequestCustomfieldService: RegisterRequestCustomfieldService,
		private NotificationService: NotificationService
	) {}

	ngOnInit(): void {
		this.LabelsService.getLabels().subscribe((labels) => {
			this.labels = { ...this.labels, ...labels };
		});
	}

	setClassification(classification: string): void {
		const value: classificationInterface = JSON.parse(classification);
		this.selectedClassification = {
			serviceType: value.serviceType || [],
			scope: value.scope || [],
			category: value.category || [],
			topic: value.topic || [],
			solutionArea: value.solutionArea || [],
		};
	}

	loadFields(): void {
		const category = this.selectedClassification.category[0]?.id;
		const topic = this.selectedClassification.topic[0]?.id;
		if (!category || !topic) {
			this.NotificationService.setNotificationMessage('Preencha categoria e tópico para buscar os campos.');
			return;
		}

		this.isLoading = true;
		this.hasSearched = true;
		this.RegisterRequestCustomfieldService.getCustomfieldSortOrder(
			String(this.classificationOptions.selectedCustomer),
			category,
			topic
		).subscribe({
			next: (fields) => {
				this.fields = (fields || []).map((field, index) => ({ ...field, position: index + 1 }));
			},
			error: () => {
				this.fields = [];
				this.NotificationService.setNotificationMessage(this.labels.lbl_an_errour_occour);
			},
			complete: () => (this.isLoading = false),
		});
	}

	moveField(index: number, direction: -1 | 1): void {
		const targetIndex = index + direction;
		if (targetIndex < 0 || targetIndex >= this.fields.length) return;
		[this.fields[index], this.fields[targetIndex]] = [this.fields[targetIndex], this.fields[index]];
		this.syncFieldPositions();
	}

	setFieldPosition(index: number, field: CustomfieldOrderItem, event: Event): void {
		const input = event.target as HTMLInputElement;
		const requestedPosition = Number(input.value);
		const targetIndex = Math.min(Math.max(Math.trunc(requestedPosition) - 1, 0), this.fields.length - 1);

		if (!Number.isFinite(requestedPosition) || requestedPosition < 1) {
			field.position = index + 1;
			return;
		}

		const [movedField] = this.fields.splice(index, 1);
		this.fields.splice(targetIndex, 0, movedField);
		this.syncFieldPositions();
	}

	private syncFieldPositions(): void {
		this.fields.forEach((field, index) => (field.position = index + 1));
	}

	save(): void {
		const category = this.selectedClassification.category[0]?.id;
		const topic = this.selectedClassification.topic[0]?.id;
		if (!category || !topic || this.fields.length === 0 || this.isSaving) return;

		this.isSaving = true;
		const fields = this.fields.map((field, index) => ({ request_field: field.request_field, sort_order: index }));
		this.RegisterRequestCustomfieldService.updateCustomfieldSortOrder(
			String(this.classificationOptions.selectedCustomer),
			category,
			topic,
			fields
		).subscribe({
			next: () => this.NotificationService.setNotificationMessage(this.labels.lbl_salvo_com_sucesso),
			error: () => this.NotificationService.setNotificationMessage(this.labels.lbl_an_errour_occour),
			complete: () => (this.isSaving = false),
		});
	}
}
